/**
 * The parts of Ayarlar that change something.
 *
 * The previous version of this screen was right to refuse placebo toggles, and
 * the answer to "almost nothing here is settable" was never to add switches -
 * it was to build the capability. Each control below is backed by a real
 * endpoint, and each one is only rendered because that endpoint exists.
 *
 * The rule from the last phase still holds and is now easier to keep: if
 * something looks pressable it does something. What changed is how much there
 * is to press.
 */

import { useCallback, useState } from "react";
import {
  changePassword,
  listSessions,
  revokeOtherSessions,
  revokeSession,
  updateDisplayName,
} from "@/api/client";
import { ApiError } from "@/api/errors";
import type { AccountSession } from "@/api/types";
import { Field } from "@/components/Field";
import { Button, Callout, Loading } from "@/components/ui";
import { Section } from "@/components/product";
import { useAuth } from "@/features/auth/AuthGate";
import {
  THEMES,
  THEME_DESCRIPTIONS,
  THEME_LABELS,
  readTheme,
  writeTheme,
  type Theme,
} from "@/app/appearance";
import { formatDateTime } from "@/lib/format";
import { useAsync } from "@/lib/useAsync";
import "./support.css";

/** Turn any failure into one safe sentence. Never the server's own words. */
function messageFor(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    if (error.status === 401) return "Mevcut parola hatalı.";
    if (error.status === 429)
      return "Çok fazla deneme yapıldı. Lütfen bir süre sonra tekrar deneyin.";
    if (error.status === 422) return fallback;
    if (error.kind === "network" || error.kind === "timeout")
      return "Sunucuya ulaşılamıyor. Lütfen tekrar deneyin.";
  }
  return fallback;
}

/* --- display name ---------------------------------------------------------- */

export function DisplayNameSetting() {
  const { user, refreshUser } = useAuth();
  const [value, setValue] = useState(user.display_name);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dirty = value.trim() !== user.display_name && value.trim() !== "";

  const save = async () => {
    setBusy(true);
    setError(null);
    setDone(false);
    try {
      await updateDisplayName(value.trim());
      // The greeting on Ana Sayfa reads the same context, so refreshing it
      // here is what makes the change visible without a new sign-in.
      await refreshUser();
      setDone(true);
    } catch (caught) {
      setError(messageFor(caught, "Ad kaydedilemedi. Lütfen tekrar deneyin."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section title="Profil">
      <Field
        label="Görünen ad"
        hint="Bu ad Ana Sayfa'da ve hesap alanlarında gösterilir."
      >
        {(props) => (
          <input
            {...props}
            type="text"
            value={value}
            maxLength={100}
            onChange={(event) => {
              setValue(event.target.value);
              setDone(false);
            }}
          />
        )}
      </Field>
      <div className="settings__actions">
        <Button variant="primary" onClick={save} busy={busy} disabled={!dirty}>
          Kaydet
        </Button>
      </div>
      {done && <Callout tone="success">Görünen adınız güncellendi.</Callout>}
      {error && <Callout tone="danger">{error}</Callout>}
      <p className="muted small settings__hint">
        E-posta adresiniz bu ekrandan değiştirilemez.
      </p>
    </Section>
  );
}

/* --- password -------------------------------------------------------------- */

export function PasswordSetting() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ revoked: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mismatch = confirm !== "" && next !== confirm;
  const ready = current !== "" && next !== "" && next === confirm;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const response = await changePassword({
        current_password: current,
        new_password: next,
      });
      setResult({ revoked: response.other_sessions_revoked });
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (caught) {
      setError(
        messageFor(caught, "Parola en az 8 karakter olmalıdır."),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section title="Parola">
      <form onSubmit={submit} className="stack-tight">
        {/*
          `autoComplete` hints, and nothing else: the values live in component
          state for the length of the request and are cleared on success. They
          are never written to storage and never appear in a URL.
        */}
        <Field label="Mevcut parola">
          {(props) => (
            <input
              {...props}
              type="password"
              value={current}
              autoComplete="current-password"
              onChange={(event) => setCurrent(event.target.value)}
            />
          )}
        </Field>
        <Field label="Yeni parola" hint="En az 8 karakter.">
          {(props) => (
            <input
              {...props}
              type="password"
              value={next}
              autoComplete="new-password"
              onChange={(event) => setNext(event.target.value)}
            />
          )}
        </Field>
        <Field
          label="Yeni parola (tekrar)"
          error={mismatch ? "Parolalar eşleşmiyor." : null}
        >
          {(props) => (
            <input
              {...props}
              type="password"
              value={confirm}
              autoComplete="new-password"
              onChange={(event) => setConfirm(event.target.value)}
            />
          )}
        </Field>
        <div className="settings__actions">
          <Button type="submit" variant="primary" busy={busy} disabled={!ready}>
            Parolayı Değiştir
          </Button>
        </div>
      </form>

      {result && (
        <Callout tone="success">
          Parolanız değiştirildi.{" "}
          {/*
            Only claimed when it actually happened. The server returns the
            count, so a user with no other devices is not told devices were
            signed out.
          */}
          {result.revoked > 0
            ? `Diğer ${result.revoked} oturum sonlandırıldı.`
            : "Bu cihazdaki oturumunuz açık kaldı."}
        </Callout>
      )}
      {error && <Callout tone="danger">{error}</Callout>}
    </Section>
  );
}

/* --- sessions -------------------------------------------------------------- */

export function SessionsSetting() {
  const fetcher = useCallback((signal: AbortSignal) => listSessions({ signal }), []);
  const { state, reload } = useAsync(fetcher);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const sessions: AccountSession[] = state.status === "success" ? state.data.sessions : [];
  const others = sessions.filter((session) => !session.current);

  const end = async (id: string) => {
    setBusy(id);
    try {
      await revokeSession(id);
      reload();
    } finally {
      setBusy(null);
    }
  };

  const endOthers = async () => {
    setBusy("others");
    try {
      await revokeOtherSessions();
      setConfirming(false);
      reload();
    } finally {
      setBusy(null);
    }
  };

  return (
    <Section title="Açık oturumlar">
      {state.status === "loading" && state.data === null && <Loading />}

      {sessions.length > 0 && (
        <ul className="sessions">
          {sessions.map((session) => (
            <li key={session.id} className="sessions__row">
              <div>
                <p className="sessions__title">
                  {/*
                    A coarse browser family from the User-Agent at sign-in, or
                    a neutral label. Never an invented device identity, never
                    an IP, never a fingerprint.
                  */}
                  {session.device_label ?? "Oturum"}
                  {session.current && <span className="sessions__badge">Bu cihaz</span>}
                </p>
                <p className="muted small">
                  Başlangıç {formatDateTime(session.created_at)} · Bitiş{" "}
                  {formatDateTime(session.expires_at)}
                </p>
              </div>
              {!session.current && (
                <Button
                  onClick={() => void end(session.id)}
                  busy={busy === session.id}
                >
                  Oturumu Sonlandır
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {others.length > 0 && (
        <div className="settings__actions">
          {confirming ? (
            <>
              <span className="muted small">
                Bu cihaz dışındaki {others.length} oturum sonlandırılsın mı?
              </span>
              <Button
                variant="danger"
                onClick={() => void endOthers()}
                busy={busy === "others"}
              >
                Evet, sonlandır
              </Button>
              <Button onClick={() => setConfirming(false)}>Vazgeç</Button>
            </>
          ) : (
            <Button onClick={() => setConfirming(true)}>
              Diğer Oturumları Sonlandır
            </Button>
          )}
        </div>
      )}

      <p className="muted small settings__hint">
        Bir oturum sonlandırıldığında o cihazdaki oturum anında geçersiz olur.
        "Beni hatırla" ile açılmış oturumlar da dahildir.
      </p>
    </Section>
  );
}

/* --- appearance ------------------------------------------------------------ */

/**
 * A tile that has locally adopted a whole theme.
 *
 * `data-theme-preview` is matched by every theme block in `tokens.css`
 * alongside `:root[data-theme=...]`, so this box computes its own `--bg`,
 * `--surface`, `--text` and `--accent` from the real palette. Nothing is
 * copied: a swatch cannot drift from the theme it advertises, and adding a
 * sixth theme needs no change here at all.
 *
 * "Sistem" is the one case with no palette of its own, so it carries no
 * attribute and previews whatever is currently in force - which is the honest
 * answer to what choosing it would give you.
 */
function ThemePreview({ theme }: { theme: Theme }) {
  return (
    <span
      className="themecard__preview"
      data-theme-preview={theme === "system" ? undefined : theme}
      aria-hidden="true"
    >
      <span className="themecard__chrome" />
      <span className="themecard__surface">
        <span className="themecard__line themecard__line--text" />
        <span className="themecard__line themecard__line--muted" />
        <span className="themecard__accent" />
      </span>
    </span>
  );
}

export function AppearanceSetting() {
  const [theme, setTheme] = useState<Theme>(() => readTheme());

  return (
    <Section title="Görünüm">
      {/*
        A radio group, not a set of toggles: the states are mutually exclusive
        and the platform's semantics say so. Applied immediately - there is
        nothing to save, and a Kaydet button for a preference you can already
        see applied would be a control with nothing to do.
      */}
      <div className="themepicker" role="radiogroup" aria-label="Görünüm">
        {THEMES.map((option) => (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={theme === option}
            className={`themecard${theme === option ? " themecard--on" : ""}`}
            onClick={() => {
              setTheme(option);
              writeTheme(option);
            }}
          >
            <ThemePreview theme={option} />
            <span className="themecard__text">
              <strong className="themecard__name">
                {THEME_LABELS[option]}
                {/* Selection is stated, not only drawn: a ring and a brighter
                    border are both colour, and colour is exactly what the
                    High Contrast user is here about. */}
                {theme === option && (
                  <span className="themecard__on" aria-hidden="true">
                    ✓ Seçili
                  </span>
                )}
              </strong>
              <small>{THEME_DESCRIPTIONS[option]}</small>
            </span>
          </button>
        ))}
      </div>
      <p className="muted small settings__hint">
        "Sistem" seçiliyken arayüz, işletim sisteminizin açık/koyu tercihini
        izler. Bu tercih yalnızca bu tarayıcıda saklanır ve hesabınıza
        kaydedilmez.
      </p>
    </Section>
  );
}
